import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  NbDialogService,
  NbMenuService,
  NbSidebarService,
} from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService, UserAccess } from '../../services/auth.service';
import { ChatPreviewService } from '../../services/chat-preview.service';
import { DozentNotification, DozentNotificationService } from '../../services/dozent-notification.service';
import { DozentSocketService } from '../../services/dozent-socket.service';
import { OrganisationService } from '../../services/organisation.service';
import { UserService } from '../../services/user.service';
import { AgentChatService } from '../../agent/agent-chat.service';
import { AgentApprovalsService } from '../../agent/agent-approvals.service';
import { ChatPreviewDialogComponent } from '../dialogs/chat-preview-dialog/chat-preview-dialog.component';
import { PrerequisiteDecisionDialogComponent } from '../dialogs/prerequisite-decision-dialog/prerequisite-decision-dialog.component';
import { LinkRequestDecisionDialogComponent } from '../dialogs/link-request-decision-dialog/link-request-decision-dialog.component';

@Component({
  selector: 'app-dashboard-header',
  templateUrl: './dashboard-header.component.html',
  styleUrl: './dashboard-header.component.scss',
})
export class DashboardHeaderComponent {
  readonly agentChat = inject(AgentChatService);
  readonly agentApprovals = inject(AgentApprovalsService);
  userMenu: any = [];
  userPictureOnly: boolean = false;

  showChatPreviewButton: boolean = false;
  showNotificationBell: boolean = false;
  unreadNotifications = 0;
  notificationsOpen = false;

  constructor(
    private sidebarService: NbSidebarService,
    private nbMenuService: NbMenuService,
    private authService: AuthService,
    private elementRef: ElementRef,
    protected userService: UserService,
    protected organisationService: OrganisationService,
    protected device: DeviceDetectorService,
    protected chatPreviewService: ChatPreviewService,
    protected notificationService: DozentNotificationService,
    private dozentSocket: DozentSocketService,
    protected nbDialogService: NbDialogService,
    protected t: TranslateService,
    protected router: Router
  ) {
    this.createMenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.notificationsOpen) return;
    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.notificationsOpen = false;
    }
  }

  ngOnInit(): void {
    this.nbMenuService
      .onItemClick()
      .pipe(filter(({ tag }) => tag === 'userMenuTag'))
      .subscribe((context: any) => {
        if (context.item.key === 'logout') {
          this.authService.logout();
        }
      });

    // Notification bell for dozent users.
    // Connect the dozent WebSocket from here so live pushes
    // (`SC_DOZENT_NOTIFICATION`) reach the bell on every page,
    // not only when the user has the messages page open. The socket
    // service is idempotent: a second connect() while OPEN is a no-op.
    // Creator users too: comment mentions and review-link comments land here.
    if (
      this.authService.hasAccess(UserAccess.DozentMessages) ||
      this.authService.hasFeatureAccess('DOZENT') ||
      this.authService.hasFeatureAccess('CONTENT_GENERATOR')
    ) {
      this.showNotificationBell = true;
      this.notificationService.loadNotifications();
      this.notificationService.unreadCount$.subscribe((count) => {
        this.unreadNotifications = count;
      });
      this.dozentSocket.connect();
    }

    // get initial route
    this.showChatPreviewButton = !this.router.url.includes('/analytics');
    setTimeout(() => {
      this.onRouteChange();
    }, 1000);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const showChatPreviewButton = !event.url.includes('/analytics');

        if (showChatPreviewButton === this.showChatPreviewButton) {
          return;
        }

        this.showChatPreviewButton = showChatPreviewButton;

        this.onRouteChange();
      });
  }

  createMenu() {
    this.userMenu = [];
    this.userMenu.push({
      title: this.t.instant('pages.products.title'),
      icon: {
        icon: 'plan',
        pack: 'al',
      },
      link: 'products',
    });

    if (this.authService.hasAccess(UserAccess.UserMenuOrganisation)) {
      this.userMenu.push({
        title: this.t.instant('pages.organisation.title'),
        icon: {
          icon: 'organisation',
          pack: 'al',
        },
        link: 'organisation',
      });
    }

    this.userMenu.push(
      {
        title: this.t.instant('pages.settings.title'),
        icon: {
          icon: 'settings',
          pack: 'al',
        },
        link: 'settings',
      },
      {
        title: this.t.instant('buttons.logout'),
        key: 'logout',
        icon: {
          icon: 'logout',
          pack: 'al',
        },
      }
    );
  }

  onRouteChange() {
    if (this.showChatPreviewButton == false) {
      this.chatPreviewService.connect(
        environment.analyticsApiKey,
        environment.analyticsBotId,
        {
          isChatOpen: false,
          additionalUserIds: [
            {
              id: this.authService.accessTokenPayload$.value.user_id ?? '',
              name: 'alphabeesUserId',
            },
            {
              id: this.organisationService.organisation?.id ?? '',
              name: 'alphabeesClientId',
            },
          ],
        }
      );
    } else {
      this.onCloseChat();
    }
  }

  get name(): string {
    if (this.device.isMobile()) {
      return '';
    }
    return this.userService.me$.value?.name ?? '';
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');

    return false;
  }

  async onStartChat() {
    this.nbDialogService.open(ChatPreviewDialogComponent, {});
  }

  onCloseChat() {
    this.chatPreviewService.closeChat();
  }

  toggleNotifications() {
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen) {
      this.notificationService.loadNotifications();
    }
  }

  onMarkAllRead() {
    this.notificationService.markAllRead();
  }

  getNotificationTitle(n: DozentNotification): string {
    const key = `components.notifications.types.${n.type}`;
    const translated = this.t.instant(key);
    if (translated !== key) {
      return translated;
    }

    const referenceKey = n.reference_type
      ? `components.notifications.types.${n.reference_type}`
      : '';
    if (referenceKey) {
      const referenceTranslated = this.t.instant(referenceKey);
      if (referenceTranslated !== referenceKey) {
        return referenceTranslated;
      }
    }

    return n.title || this.t.instant('components.notifications.title');
  }

  getNotificationBody(n: DozentNotification): string {
    const meta = n.metadata || {};
    if (n.type === 'moodle_sync_success') {
      const completed = this.toNotificationCount(meta['completed'], this.extractFirstNumber(n.body), 0);
      const key = completed === 1
        ? 'components.notifications.messages.moodleSyncSuccessOne'
        : 'components.notifications.messages.moodleSyncSuccessMany';
      return this.t.instant(key, { completed });
    }

    if (n.type === 'moodle_sync_warning') {
      const completed = this.toNotificationCount(meta['completed'], undefined, 0);
      const failed = this.toNotificationCount(meta['failed'], undefined, 0);
      const total = this.toNotificationCount(meta['total'], completed + failed, completed + failed);
      return this.t.instant('components.notifications.messages.moodleSyncWarning', {
        completed,
        failed,
        total,
      });
    }

    if (n.type === 'moodle_sync_error') {
      return this.t.instant('components.notifications.messages.moodleSyncError');
    }

    if (n.type === 'exercise_import_success') {
      const done = this.toNotificationCount(meta['done'], this.extractFirstNumber(n.body), 0);
      const key = done === 1
        ? 'components.notifications.messages.exerciseImportSuccessOne'
        : 'components.notifications.messages.exerciseImportSuccessMany';
      return this.t.instant(key, { done });
    }

    if (n.type === 'exercise_import_warning') {
      const done = this.toNotificationCount(meta['done'], undefined, 0);
      const failed = this.toNotificationCount(meta['failed'], undefined, 0);
      const total = this.toNotificationCount(meta['total'], done + failed, done + failed);
      return this.t.instant('components.notifications.messages.exerciseImportWarning', {
        done,
        failed,
        total,
      });
    }

    if (n.type === 'exercise_import_error') {
      return this.t.instant('components.notifications.messages.exerciseImportError');
    }

    if (n.type === 'creator_comment_mention') {
      return this.t.instant('components.notifications.messages.creatorCommentMention', {
        by: meta['by'] || n.body || '—',
        title: meta['title'] || n.title || '—',
      });
    }

    if (n.type === 'creator_review_comment') {
      return this.t.instant('components.notifications.messages.creatorReviewComment', {
        by: meta['by'] || n.body || '—',
        title: meta['title'] || n.title || '—',
        link: meta['review_link_name'] || '—',
      });
    }

    if (n.type === 'bot_link_request') {
      return this.t.instant('components.notifications.messages.botLinkRequest', {
        requester: meta['requester_name'] || '—',
        twin: meta['dozent_bot_name'] || '—',
        target: meta['target_tutor_name'] || '—',
      });
    }

    if (n.type === 'bot_link_request_approved') {
      return this.t.instant(
        'components.notifications.messages.botLinkRequestApproved',
        {
          twin: meta['dozent_bot_name'] || '—',
          target: meta['target_tutor_name'] || '—',
        },
      );
    }

    if (n.type === 'bot_link_request_rejected') {
      const base = this.t.instant(
        'components.notifications.messages.botLinkRequestRejected',
        {
          twin: meta['dozent_bot_name'] || '—',
          target: meta['target_tutor_name'] || '—',
        },
      );
      return meta['decision_note'] ? `${base} ${meta['decision_note']}` : base;
    }

    return n.body || '';
  }

  private toNotificationCount(value: unknown, fallback: number | undefined, defaultValue: number): number {
    const parsed = Number(value ?? fallback ?? defaultValue);
    return Number.isFinite(parsed) ? parsed : defaultValue;
  }

  private extractFirstNumber(value?: string): number | undefined {
    const match = value?.match(/\d+/);
    return match ? Number(match[0]) : undefined;
  }

  onNotificationClick(n: DozentNotification) {
    if (!n.is_read) {
      this.notificationService.markRead(n.id);
    }
    // Remove it from the local list so it disappears from the popover
    const current = this.notificationService.notifications$.value.filter(
      (x) => x.id !== n.id,
    );
    this.notificationService.notifications$.next(current);

    this.notificationsOpen = false;

    // Navigate to the exact source of the notification
    switch (n.reference_type) {
      case 'conversation':
        this.router.navigate(['/dozent/messages'], {
          queryParams: n.reference_id ? { conversationId: n.reference_id } : {},
        });
        break;
      case 'submission': {
        // Submissions are reviewed in the SubmissionReviewDialog,
        // which is opened from the participants page (not the
        // exercises overview). The dialog needs the assignment_id —
        // we stashed it in `metadata` at notification-create time so
        // we can deep-link without an extra round-trip.
        const meta = n.metadata || {};
        const assignmentId = meta['assignment_id'];
        const submissionId = meta['submission_id'] || n.reference_id;
        if (assignmentId) {
          this.router.navigate(['/dozent/participants'], {
            queryParams: { assignmentId, submissionId },
          });
        } else {
          // Older notifications without metadata — fall back to the
          // overview so the link doesn't dead-end.
          this.router.navigate(['/dozent/participants']);
        }
        break;
      }
      case 'learning_suggestion':
        this.router.navigate(['/dozent/faqs'], {
          queryParams: n.reference_id ? { suggestionId: n.reference_id } : {},
        });
        break;
      case 'faq':
        this.router.navigate(['/dozent/faqs'], {
          queryParams: n.reference_id ? { faqId: n.reference_id } : {},
        });
        break;
      case 'moodle_sync':
        this.router.navigate(['/tutors/placements']);
        break;
      case 'exercise_import':
        this.router.navigate(['/dozent/exercises']);
        break;
      case 'creator_item': {
        // a comment in the Creator: open the content, the course editor
        // shows its comments (creator_comment_ops)
        const meta = n.metadata || {};
        const item = meta['item_id'] || n.reference_id;
        if (!item) break;
        const kind = meta['kind'];
        const ac = kind === 'elearning_course' || kind === 'microlearning';
        this.router.navigate(['/creator/workspace'], {
          queryParams: ac ? { item, kind, title: meta['title'] || n.title } : { item },
        });
        break;
      }
      case 'bot_link_request':
        // Twin↔Tutor link request: admins/managers decide right here in a
        // dialog (approve/reject); the requester sees it read-only with the
        // decision. No page navigation.
        this.nbDialogService.open(LinkRequestDecisionDialogComponent, {
          context: { requestId: n.reference_id || undefined },
          closeOnBackdropClick: true,
        });
        break;
      case 'course_prerequisite_link': {
        // Entschieden wird direkt hier — dieselbe Form wie beim
        // Twin↔Tutor-Antrag darueber. Vorher sprang die Meldung nur in die
        // Moodle-Uebersicht und oeffnete die Verknuepfungen des Kurses; welche
        // Zeile gemeint war, musste man selbst finden, und der Status blieb
        // ausstehend. Eine Meldung, die "bestaetigen" heisst, muss das
        // Bestaetigen anbieten.
        //
        // `reference_id` ist die Verknuepfung, `target_course_id` steht seit
        // dem Anlegen in der Metadata — zusammen genug, um die eine Zeile zu
        // holen, ohne einen eigenen Endpunkt dafuer.
        const meta = n.metadata || {};
        const courseId = meta['target_course_id'];
        if (n.reference_id && courseId) {
          this.nbDialogService.open(PrerequisiteDecisionDialogComponent, {
            context: { linkId: n.reference_id, targetCourseId: courseId },
            closeOnBackdropClick: true,
          });
        } else {
          // Aeltere Meldungen ohne Metadata: wenigstens an den richtigen Ort,
          // statt ins Leere.
          this.router.navigate(['/moodle']);
        }
        break;
      }
      case 'analytics_session':
        // Negative Rückmeldung: der Fall gehört in den Konversations-Browser,
        // wo das Gespräch dazu steht. Ohne diesen Zweig landete die Meldung
        // über `default` bei den Dozenten-Nachrichten, wo es sie nicht gibt.
        this.router.navigate(['/analytics/conversations']);
        break;
      default:
        this.router.navigate(['/dozent/messages']);
    }
  }
}
